<template>
  <div class="pa-4" :class="{ 'accruals-ingestion--embedded': embedded }" data-testid="accruals-ingestion">
    <div v-if="!embedded" class="d-flex align-center mb-4">
      <h3 class="font-weight-medium">Accruals source data</h3>
      <v-spacer></v-spacer>
      <v-btn icon class="shadow-off" @click="refreshAll" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-alert v-if="error" type="error" dense text class="mb-4" data-testid="accruals-ingestion__error">
      {{ error }}
    </v-alert>
    <p class="caption grey--text mb-4">
      Land a file, map source columns to canonical destination fields, then ingest. Each dataset runs as an independent SilkRoute flow.
    </p>
    <v-tabs v-model="tab" color="primary" class="mb-4">
      <v-tab v-for="entity in entities" :key="entity.type">
        {{ entity.label }}
        <v-chip x-small class="ml-2" :color="statusColor(entity.type)" text-color="white">
          {{ statusLabel(entity.type) }}
        </v-chip>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item v-for="entity in entities" :key="entity.type">
        <v-card class="shadow-off pa-4">
          <div class="d-flex align-center mb-3">
            <div>
              <div class="subtitle-2">{{ entity.label }}</div>
              <div class="caption grey--text">
                Flow {{ entity.flow }} · raw {{ rawCount(entity.type) }} · published {{ canonicalCount(entity.type) }}
                <span v-if="mappingConfigured(entity.type)" class="green--text"> · mapping saved</span>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-file-input
              v-model="files[entity.type]"
              hide-details
              dense
              outlined
              accept=".xlsx,.xls,.csv"
              prepend-icon="mdi-paperclip"
              class="mr-2"
              style="max-width: 280px"
              label="Excel / CSV"
            ></v-file-input>
            <v-btn class="mr-2 shadow-off" :loading="uploading === entity.type" @click="upload(entity.type)">
              Land file
            </v-btn>
            <v-btn
              color="primary"
              class="shadow-off white--text"
              :disabled="isInProgress(entity.type) || !rawCount(entity.type) || !mappingConfigured(entity.type)"
              :loading="starting === entity.type"
              @click="ingest(entity.type)"
            >
              Ingest
            </v-btn>
          </div>

          <v-card
            v-if="rawCount(entity.type)"
            outlined
            class="mb-4 pa-3"
            data-testid="accruals-ingestion__mapping"
          >
            <div class="d-flex align-center mb-2">
              <div class="subtitle-2">Column mapping</div>
              <v-spacer></v-spacer>
              <v-chip x-small :color="mappingComplete ? 'green' : 'orange'" text-color="white">
                {{ mappingComplete ? "ready" : "required fields pending" }}
              </v-chip>
            </div>
            <p class="caption grey--text mb-3">
              Destination fields are fixed canonical names. Map each to a column from your uploaded file (source headers are slugified on land).
            </p>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Destination (canonical)</th>
                  <th style="width: 90px">Required</th>
                  <th>Source column</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in mappingRows" :key="row.destination">
                  <td class="text-no-wrap">{{ row.destination }}</td>
                  <td>
                    <v-icon v-if="row.required" small color="red">mdi-asterisk</v-icon>
                    <span v-else class="grey--text caption">optional</span>
                  </td>
                  <td>
                    <v-select
                      v-model="row.source"
                      :items="sourceHeaderItems"
                      item-text="label"
                      item-value="value"
                      dense
                      hide-details
                      outlined
                      clearable
                      placeholder="Select source column"
                      :data-testid="`mapping-${row.destination}`"
                    ></v-select>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="d-flex justify-end mt-3">
              <v-btn
                color="primary"
                class="shadow-off white--text"
                :loading="savingMapping"
                :disabled="!mappingRows.length"
                @click="saveMapping(entity.type)"
              >
                Save mapping
              </v-btn>
            </div>
          </v-card>

          <v-simple-table dense>
            <thead>
              <tr>
                <th v-for="col in columns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="row._id || index">
                <td v-for="col in columns" :key="col">{{ row[col] }}</td>
              </tr>
              <tr v-if="!rows.length">
                <td :colspan="Math.max(columns.length, 1)" class="grey--text">No published rows yet for this entity.</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
export default {
  name: "AccrualsIngestion",
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      tab: 0,
      files: {},
      uploading: null,
      starting: null,
      pollTimer: null,
      mappingRows: [],
      entities: [
        { type: "open_po", label: "Purchase orders", flow: "accruals_open_po" },
        { type: "ap_postings", label: "Accounts payable", flow: "accruals_ap_postings" },
        { type: "vendor_master", label: "Vendor master", flow: "accruals_vendor_master" },
        { type: "accruals_ledger", label: "Accruals ledger", flow: "accruals_ledger" },
      ],
    };
  },
  computed: {
    projectId() {
      return this.$route.params.id;
    },
    cid() {
      return this.$route.query.cid || this.$store.state.client;
    },
    accruals() {
      return this.$store.state.m_financialclose?.accruals || {};
    },
    loading() {
      return !!this.accruals.loading;
    },
    savingMapping() {
      return !!this.accruals.savingMapping;
    },
    error() {
      return this.accruals.error;
    },
    rows() {
      return this.accruals.rows || [];
    },
    columns() {
      return this.accruals.columns || [];
    },
    summary() {
      return this.accruals.summary?.entities || {};
    },
    flowEntities() {
      return this.accruals.flowStatus?.entities || {};
    },
    normalization() {
      return this.accruals.normalization || {};
    },
    sourceHeaderItems() {
      const meta = new Set(['_id', 'project_id', 'uploaded_at', '__source_headers']);
      const headers = (this.normalization.detected_source_headers || []).filter(
        (slug) => slug && !meta.has(String(slug))
      );
      const labels = this.normalization.header_labels || {};
      return headers.map((slug) => ({
        value: slug,
        label: labels[slug] ? `${labels[slug]} (${slug})` : slug,
      }));
    },
    mappingComplete() {
      const required = (this.normalization.canonical_fields || []).filter((field) => field.required);
      if (!required.length) {
        return this.mappingRows.some((row) => row.source);
      }
      return required.every((field) => {
        const row = this.mappingRows.find((item) => item.destination === field.destination);
        return row && row.source;
      });
    },
    anyInProgress() {
      return this.entities.some((entity) => this.isInProgress(entity.type));
    },
  },
  watch: {
    tab() {
      this.loadTable();
      this.loadMapping();
    },
    anyInProgress(value) {
      if (value) this.startPoll();
      else this.stopPoll();
    },
    normalization: {
      deep: true,
      handler(value) {
        this.syncMappingRows(value);
      },
    },
  },
  created() {
    this.refreshAll();
  },
  beforeDestroy() {
    this.stopPoll();
  },
  methods: {
    currentEntity() {
      return this.entities[this.tab] || this.entities[0];
    },
    rawCount(type) {
      return (this.summary[type] && this.summary[type].raw_count) || 0;
    },
    canonicalCount(type) {
      return (this.summary[type] && this.summary[type].canonical_count) || 0;
    },
    mappingConfigured(type) {
      return !!(this.summary[type] && this.summary[type].mapping_configured);
    },
    statusLabel(type) {
      return (this.flowEntities[type] && this.flowEntities[type].status) || "not_started";
    },
    statusColor(type) {
      const status = this.statusLabel(type);
      if (status === "completed") return "green";
      if (status === "in-progress") return "orange";
      if (status === "failed") return "red";
      return "grey";
    },
    isInProgress(type) {
      return this.statusLabel(type) === "in-progress";
    },
    syncMappingRows(config) {
      const canonical = (config && config.canonical_fields) || [];
      const requiredByDest = {};
      canonical.forEach((field) => {
        requiredByDest[field.destination] = field.required;
      });
      const rows = (config && config.field_mappings) || [];
      this.mappingRows = rows.map((row) => ({
        destination: row.destination,
        source: row.source || "",
        required: !!requiredByDest[row.destination],
      }));
    },
    startPoll() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.$store.dispatch("accruals/loadFlowStatus", { projectId: this.projectId, cid: this.cid });
      }, 5000);
    },
    stopPoll() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    async loadMapping() {
      const entity = this.currentEntity();
      if (!this.rawCount(entity.type)) {
        this.mappingRows = [];
        this.$store.commit("accruals/setNormalization", null);
        return;
      }
      try {
        await this.$store.dispatch("accruals/loadNormalizationConfig", {
          projectId: this.projectId,
          cid: this.cid,
          entityType: entity.type,
        });
      } catch (err) {
        // Store already captured the error.
      }
    },
    async refreshAll() {
      await Promise.all([
        this.$store.dispatch("accruals/loadSummary", { projectId: this.projectId, cid: this.cid }),
        this.$store.dispatch("accruals/loadFlowStatus", { projectId: this.projectId, cid: this.cid }),
      ]);
      await this.loadTable();
      await this.loadMapping();
    },
    async loadTable() {
      const entity = this.currentEntity();
      try {
        await this.$store.dispatch("accruals/loadSourceData", {
          projectId: this.projectId,
          cid: this.cid,
          entityType: entity.type,
          page: 1,
          limit: 25,
        });
      } catch (err) {
        // Keep the previous table rows; store already captured the envelope message.
      }
    },
    async upload(entityType) {
      const file = this.files[entityType];
      if (!file) {
        this.$store.commit("accruals/setError", "Choose a file before landing");
        return;
      }
      this.uploading = entityType;
      try {
        await this.$store.dispatch("accruals/uploadSource", {
          projectId: this.projectId,
          cid: this.cid,
          entityType,
          file,
        });
        this.files[entityType] = null;
        await this.refreshAll();
      } finally {
        this.uploading = null;
      }
    },
    async saveMapping(entityType) {
      try {
        await this.$store.dispatch("accruals/saveNormalizationConfig", {
          projectId: this.projectId,
          cid: this.cid,
          entityType,
          sourceKind: "file_upload",
          fieldMappings: this.mappingRows.map((row) => ({
            destination: row.destination,
            source: row.source || "",
          })),
        });
        await Promise.all([
          this.$store.dispatch("accruals/loadSummary", { projectId: this.projectId, cid: this.cid }),
          this.$store.dispatch("accruals/loadNormalizationConfig", {
            projectId: this.projectId,
            cid: this.cid,
            entityType,
          }),
        ]);
      } catch (err) {
        // Store already surfaced the error.
      }
    },
    async ingest(entityType) {
      this.starting = entityType;
      try {
        await this.$store.dispatch("accruals/startIngestion", {
          projectId: this.projectId,
          cid: this.cid,
          entityType,
        });
        await this.refreshAll();
      } finally {
        this.starting = null;
      }
    },
  },
};
</script>
